import { useState } from 'react';
import { useLocation, Link } from 'react-router';
import { AlertCircle, X, ArrowRight } from 'lucide-react';
import { useEbayPublishability } from '@/hooks/useEbayPublishability';

/**
 * Persistent eBay-not-connected banner — closes A3 in
 * docs/UX_ANALYSIS.md. Renders on the pages where publishing + sales
 * tracking are the user's primary task (Dashboard / Inventory /
 * Listings / Transactions) when their eBay connection isn't yet set
 * up. Hidden on:
 *
 * - `/settings/integrations` — that's where the user goes to fix it,
 *   the in-page Settings card is the primary call-to-action there.
 * - Marketing / auth pages — the FosAppShell already doesn't wrap
 *   those, so this just-in-case check is a safety net.
 *
 * Dismissal lives in sessionStorage (not localStorage) so the banner
 * reappears each session if the user still hasn't connected — but
 * doesn't nag within a single session after they've acknowledged it.
 */
const SHOW_ON_PREFIXES = ['/', '/inventory', '/listings', '/transactions'];
const HIDE_ON_PREFIXES = ['/settings', '/auth', '/onboarding'];
const DISMISS_KEY = 'forvisibility.ebay-banner.dismissed.v1';

function shouldShowOnPath(pathname: string): boolean {
  // Hide first — settings is a child of `/` so prefix-match would
  // otherwise show the banner there.
  if (HIDE_ON_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return false;
  }
  return SHOW_ON_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function EbayNotConnectedBanner() {
  const location = useLocation();
  const { reason, isLoading } = useEbayPublishability();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  });

  if (isLoading) return null;
  if (dismissed) return null;
  if (reason !== 'not-connected') return null;
  if (!shouldShowOnPath(location.pathname)) return null;

  function dismiss() {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* sessionStorage may be unavailable (private browsing, etc.);
       * the in-memory state still hides the banner for this session. */
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 border-b border-fos-amber-bg bg-fos-amber-bg px-5 py-2 text-[12px] text-fos-amber"
    >
      <AlertCircle size={14} className="flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <span className="font-medium">eBay not connected.</span>{' '}
        <span className="text-fos-text-secondary">
          Publishing, sales tracking, returns, and 1099-K reporting all flow through your eBay
          connection. Connect now to unlock the full workflow.
        </span>
      </div>
      <Link
        to="/settings/integrations"
        className="hover:bg-fos-amber-bg-strong inline-flex flex-shrink-0 items-center gap-1 rounded-fos border border-fos-amber px-2 py-1 text-[11px] font-medium"
      >
        Connect eBay
        <ArrowRight size={11} />
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss eBay connection reminder"
        className="flex-shrink-0 text-fos-text-muted transition-colors duration-fos hover:text-fos-text-primary"
      >
        <X size={14} />
      </button>
    </div>
  );
}
